import { localTzDate, tzDate } from "shared/utils/dayjs";

import { WeekObject, CalendarSchedule, DayName } from "./types";

export function formatToLocalHour(date: Date | string): string {
  return tzDate(date).format("HH:mm");
}

export function formatToLocalDateRange(startTime: Date | string, endDate: Date | string): string {
  return `${formatToLocalHour(startTime)}–${formatToLocalHour(endDate)}`;
}

export function startOfWeek(date: Date): Date {
  return tzDate(date).startOf("week").toDate();
}

export function endOfWeek(date: Date): Date {
  return tzDate(date).endOf("week").toDate();
}

// CalendarSchedule.start_time/end_time are `string | Date` (ISO strings from
// V2 mappers, real Dates from the midnight clamp in daysToWeekObject). Helpers
// accept the union and rely on tzDate / dayjs / `new Date(...)` to coerce
// internally.
type DateLike = Date | string;

export function isSameDay(date1: DateLike, date2: DateLike) {
  return tzDate(date1).isSame(tzDate(date2), "day");
}

export function isSameWeek(date1: DateLike, date2: DateLike) {
  return tzDate(date1).isSame(tzDate(date2), "week");
}

export function addDay(date: DateLike, num: number): Date {
  return tzDate(date).add(num, "days").toDate();
}
export function addWeek(date: DateLike, num: number): Date {
  return tzDate(date).add(num, "weeks").toDate();
}

/**
 * The toolbar's week label in the active language: "6 – 12 Okt 2026",
 * "29 Sep – 5 Okt 2026", or "29 Des 2025 – 4 Jan 2026" across a year.
 */
export function formatWeekRange(startWeek: Date): string {
  const start = localTzDate(startWeek);
  const end = localTzDate(endOfWeek(startWeek));
  if (start.year() !== end.year()) {
    return `${start.format("D MMM YYYY")} – ${end.format("D MMM YYYY")}`;
  }
  if (start.month() !== end.month()) {
    return `${start.format("D MMM")} – ${end.format("D MMM YYYY")}`;
  }
  return `${start.format("D")} – ${end.format("D MMM YYYY")}`;
}

// Indexed by dayjs's fixed native .day() (0=Sunday..6=Saturday) — NOT
// week-start-dependent, always correct regardless of which day the calendar
// visually starts on.
const DAY_NAMES: DayName[] = ["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"];

/**
 * Sorts a week's events onto the day each STARTS on (Jakarta), dropping any
 * outside the week.
 */
export const daysToWeekObject = <T extends CalendarSchedule>(events: T[], startWeek: Date) => {
  const dayNames = DAY_NAMES;

  const weekObject: WeekObject<T> = {
    sunday: [],
    monday: [],
    tuesday: [],
    wednesday: [],
    thursday: [],
    friday: [],
    saturday: [],
  };

  if (events == null) {
    return weekObject;
  }
  // The week's bounds, resolved once. Comparing raw instants against them is
  // the same test isSameWeek made per event, without two timezone conversions
  // for every class.
  const weekStart = tzDate(startWeek).startOf("week");
  const weekStartMs = weekStart.valueOf();
  const weekEndMs = weekStart.endOf("week").valueOf();

  for (const event of events) {
    const startMs = new Date(event.start_time).getTime();
    if (startMs < weekStartMs || startMs > weekEndMs) {
      continue;
    }

    // Every event renders on its START day. An event whose end lands on the
    // next calendar day (a 23:00 class ending at or past midnight) used to be
    // treated as multi-day and split into zero-duration midnight blocks that
    // rendered display:none, so late-night classes vanished from the calendar.
    // Ending exactly at next midnight is a normal same-day event; an end past
    // midnight is clamped to the day boundary so the block stays visible
    // (detail views keep the true end time).
    //
    // Always a COPY: the clamp rewrites end_time, and the originals belong to
    // the react-query cache.
    const start = tzDate(event.start_time);
    const weekObjectKey: DayName = dayNames[start.day()];
    const nextMidnight = start.add(1, "day").startOf("day");
    if (tzDate(event.end_time).isAfter(nextMidnight)) {
      weekObject[weekObjectKey].push({ ...event, end_time: nextMidnight.toDate() });
    } else {
      weekObject[weekObjectKey].push({ ...event });
    }
  }

  return weekObject;
};
